import type { GetQuestionRelatedReturn } from "../../../app/storyblok/[localeString]/support-hub/api/question/[questionUuid]/related/route";
import type {
  GetQuestionReturn,
  QuestionDetail,
} from "../../../app/storyblok/[localeString]/support-hub/api/question/[questionUuid]/route";
import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useSearchActive } from "../../../helpers/SearchActiveContext";
import { env } from "../../../helpers/env.mjs";
import { getStoryUrl } from "../../../helpers/getStoryUrl.mjs";
import { getValueOrFallback } from "../../../helpers/getValueOrFallback";
import { isSbRichTextNotEmpty } from "../../../helpers/isSbRichTextNotEmpty";
import { notNullish } from "../../../helpers/notNullish";
import { renderSbText } from "../../../helpers/renderSbText";
import { EMPTY_LINK_URL } from "../../../helpers/resolveLinkUrl";
import { useCrhPageSettings } from "../CrhPageContext/CrhPageContext";
import { QuestionDetail as QuestionDetailComponent } from "../QuestionDetail/QuestionDetail";
import { OnSelectQuestionProps } from "../QuestionDetailModalRoot/QuestionDetailModalRoot";
import {
  CosmosButton,
  CosmosIconClose,
  CosmosSpinner,
  CosmosText,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import { Route } from "next";
import React from "react";
import { useEffect, useState, useTransition } from "react";
import ReactModal from "react-modal";
import styles from "./QuestionDetailModal.module.css";

const cx = classNames.bind(styles);

type QuestionDetailModalProps = {
  onClose?: () => void;
  /**
   * When falsy the modal is closed. The component MUST stay mounted across
   * open/close transitions so react-modal can run its close lifecycle (focus
   * restore, transition cleanup, registering the question modal as closed).
   */
  questionUuid: string | null | undefined;
  localeString: string;
  onSelectQuestion: ({
    questionUuid,
    questionName,
    questionTopic,
  }: OnSelectQuestionProps) => void;
  closeButtonLabel: string;
};

export function QuestionDetailModal({
  localeString,
  questionUuid,
  onClose,
  onSelectQuestion,
  closeButtonLabel,
}: QuestionDetailModalProps) {
  const settings = useCrhPageSettings();
  const contactPageRouter = useContactPageRouterContext();
  const { registerQuestionModalOpen } = useSearchActive();

  const [question, setQuestion] = useState<{
    questionDetails: QuestionDetail;
    relatedQuestions: GetQuestionRelatedReturn["data"];
  } | null>(null);
  const [loading, startTransition] = useTransition();
  const [error, setError] = useState<Error | null>(null);

  /**
   * Prevent body scroll when modal is open
   * This has been moved to a ReactModal__Body--open class on the body
   * */
  // useEffect(() => {
  //   if (questionUuid) {
  //     document.body.style.overflow = "hidden";
  //   } else {
  //     document.body.style.overflow = "";
  //   }

  //   return () => {
  //     document.body.style.overflow = "";
  //   };
  // }, [questionUuid]);

  useEffect(() => {
    setError(null);
    setQuestion(null);

    async function fetchData() {
      const questionDetails = await fetch(
        `/${localeString}/support-hub/api/question/${questionUuid}` satisfies Route,
      )
        .then((response) => response.json() as Promise<GetQuestionReturn>)
        .then((result) => result.data);

      if (!questionDetails) {
        setError(new Error("No question found"));
        return;
      }

      const relatedQuestions = await fetch(
        `/${localeString}/support-hub/api/question/${questionUuid}/related` satisfies Route,
      )
        .then(
          (response) => response.json() as Promise<GetQuestionRelatedReturn>,
        )
        .then((result) => result.data);

      setQuestion({
        questionDetails,
        relatedQuestions,
      });
    }

    if (questionUuid) {
      startTransition(() => fetchData().catch(console.error));
    } else {
      setQuestion(null);
    }
  }, [questionUuid, localeString]);

  const questionModalOpen = Boolean(questionUuid);
  useEffect(() => {
    if (!questionModalOpen) return;
    return registerQuestionModalOpen();
  }, [questionModalOpen, registerQuestionModalOpen]);

  return (
    <ReactModal
      isOpen={questionModalOpen}
      onRequestClose={onClose}
      className={cx("modal-layout")}
      portalClassName={cx("modal-portal")}
      overlayClassName={cx("modal-overlay")}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      shouldFocusAfterRender
      shouldReturnFocusAfterClose
      // We apply `inert` ourselves on specific layout regions, so we don't
      // want react-modal toggling `aria-hidden` on `#support-hub-app`.
      ariaHideApp={false}
      // Disable react-modal's `<body>` class management — its ref counter
      // can desync with React lifecycle and leave the class stuck. The body
      // class is driven by `SearchActiveContextProvider` instead.
      bodyOpenClassName={null}
    >
      {(() => {
        if (loading) {
          return (
            <div className={cx("spinner-container")}>
              <CosmosSpinner appearance="light" className={cx("spinner")} />
            </div>
          );
        }
        if (error) {
          return <CosmosText>Error: {error.message}</CosmosText>;
        }
        if (question) {
          const { questionDetails } = question;

          // Use supportLinkDev for staging/development, supportLink for production
          // Make sure to update anywhere else in the codebase that uses supportLink.
          const supportLink =
            env.NEXT_PUBLIC_ZENDESK_ENV === "production"
              ? questionDetails.supportLink
              : (questionDetails.supportLinkDev ?? questionDetails.supportLink);

          const supportBannerHeadline = getValueOrFallback(
            questionDetails.supportBannerHeadline,
            settings.supportBannerHeadline,
          );

          const supportBannerBody = getValueOrFallback(
            questionDetails.supportBannerBody,
            settings.supportBannerBody,
          );

          // Custom CTAs
          const customCtas = (questionDetails.customCTAs ?? []).map((item) => ({
            label: item.label,
            href: item.link.url !== EMPTY_LINK_URL ? item.link.url : undefined,
            icon: item.icon,
          }));

          return (
            <div className={cx("modal")}>
              <CosmosButton
                onClick={onClose}
                kind="bare"
                size="small"
                shape="circle"
                iconPlacement="after"
                className={cx("close-button")}
                button-aria-label={closeButtonLabel}
              >
                <CosmosIconClose
                  slot="icon"
                  className={cx("close-button-icon")}
                />
              </CosmosButton>
              <QuestionDetailComponent
                supportBannerCtaLabel={settings.supportBannerCtaLabel}
                supportBannerHeadline={supportBannerHeadline}
                supportBannerBody={supportBannerBody}
                supportLink={
                  supportLink && supportLink.url !== EMPTY_LINK_URL
                    ? supportLink.url
                    : undefined
                }
                fullAnswerButtonLink={
                  isSbRichTextNotEmpty(questionDetails.answer)
                    ? getStoryUrl(
                        {
                          default_full_slug: questionDetails._default_full_slug,
                          content: { component: "faq" },
                        },
                        localeString,
                      )
                    : undefined
                }
                question={questionDetails.question}
                topicNames={(questionDetails.crhTopics ?? [])
                  .filter(notNullish)
                  .map((topic) => topic.topicName)}
                customCtas={customCtas}
                shortAnswer={renderSbText(
                  questionDetails.summaryAnswer,
                  localeString,
                )}
                relatedQuestions={question.relatedQuestions.map(
                  (relatedQuestion) => ({
                    uuid: relatedQuestion._uuid,
                    question: relatedQuestion.question,
                    name: relatedQuestion._name, // needed for analytics as unique identifier
                    topics: (relatedQuestion.crhTopics ?? [])
                      .filter(notNullish)
                      .map((topic) => topic.topicName),
                    href: contactPageRouter.selectQuestionUrl({
                      uuid: relatedQuestion._uuid,
                    }),
                  }),
                )}
                onSelectQuestion={onSelectQuestion}
                fullAnswerButtonLabel={settings.fullAnswerButtonLabel ?? ""}
              />
            </div>
          );
        }
        return (
          <div className={cx("modal")}>
            <p>No question found</p>
          </div>
        );
      })()}
    </ReactModal>
  );
}
